import { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '../api.js';
import { useCart } from '../cart.jsx';
import { Doodle, Reveal, SprinkleBurst } from '../Doodles.jsx';

/* Color indicativo de cada masa (para el punto del selector de sabor) */
const MASA_COLOR = {
  'Choco chips clásica': '#C9924A',
  'Nutella': '#A6713B',
  'Red Velvet': '#A23A3D',
  'Lotus': '#B8773F',
};
/* Matriz de imágenes (ilustración FLUX) por sabor × color de cobertura.
   La masa es la misma; lo que cambia es el color real del glaseado. */
const FLAVOR_KEY = {
  'Choco chips clásica': 'choco',
  'Nutella': 'nutella',
  'Red Velvet': 'redvelvet',
  'Lotus': 'lotus',
};
const COLOR_KEY = {
  'Crema': 'crema', 'Rosa': 'rosa', 'Menta': 'menta',
  'Lila': 'lila', 'Amarillo': 'amarillo', 'Chocolate': 'chocolate',
};
const imgFor = (masa, cobertura) =>
  `/brand/cookie/cc-${FLAVOR_KEY[masa] || 'choco'}-${COLOR_KEY[cobertura] || 'crema'}.webp`;
const COBERTURAS = [
  { nombre: 'Crema', color: '#FBEBD3' },
  { nombre: 'Rosa', color: '#F4B8C7' },
  { nombre: 'Menta', color: '#A8D5BA' },
  { nombre: 'Lila', color: '#CDB8E8' },
  { nombre: 'Amarillo', color: '#FCE38A' },
  { nombre: 'Chocolate', color: '#6E4A39' },
];
const SPRINKLE_COLORS = ['#F4B8C7', '#A8D5BA', '#FCE38A', '#FEC8A5', '#4A2A45'];
const DARK_COBERTURAS = ['Chocolate', 'Lila'];
// Anclaje del ÓVALO de la superficie del glaseado en cada imagen (viewBox 600),
// medido sobre la imagen real. Los toppings se colocan y recortan a este óvalo
// para que siempre queden sobre el glaseado y no "flotando" fuera.
// Estructura por sabor y TAMAÑO (lista para imágenes de 23 cm; de momento ambos
// tamaños comparten imagen, así que comparten anclaje).
const FROST_ANCHOR = {
  'Choco chips clásica': { '15': { cx: 300, cy: 237, rx: 160, ry: 40 } },
  'Nutella': { '15': { cx: 300, cy: 260, rx: 162, ry: 40 } },
  'Red Velvet': { '15': { cx: 300, cy: 250, rx: 150, ry: 36 } },
  'Lotus': { '15': { cx: 300, cy: 248, rx: 158, ry: 38 } },
};
const sizeKey = (tamano) => (String(tamano || '').includes('23') ? '23' : '15');
function anchorFor(masa, tamano) {
  const byFlavor = FROST_ANCHOR[masa] || FROST_ANCHOR['Choco chips clásica'];
  return byFlavor[sizeKey(tamano)] || byFlavor['15'];
}

/* Vista previa: imagen base por sabor + overlay SVG (tinte de cobertura, sprinkles, mensaje, topper).
   El tinte es una elipse difuminada anclada al centro del glaseado de cada sabor (robusto a pequeñas
   diferencias de composición entre imágenes). */
function CookiePreview({ masa, cobertura, tamano, sprinkles, topper, mensaje }) {
  const img = imgFor(masa, cobertura);
  const dark = DARK_COBERTURAS.includes(cobertura);
  const textColor = dark ? '#FFF7F0' : '#4A2A45';
  const a = anchorFor(masa, tamano);
  const clipId = `frost-${FLAVOR_KEY[masa] || 'x'}-${sizeKey(tamano)}`;
  // sprinkles repartidos uniformemente DENTRO del óvalo del glaseado (espiral filotáctica)
  const N = 16;
  const spr = Array.from({ length: N }).map((_, i) => {
    const ang = i * 2.39996323;                 // ángulo áureo
    const rr = Math.sqrt((i + 0.5) / N) * 0.86; // radio normalizado, deja margen al borde
    return {
      x: a.cx + Math.cos(ang) * a.rx * rr,
      y: a.cy + Math.sin(ang) * a.ry * rr,
      r: (i * 53) % 180,
      c: SPRINKLE_COLORS[i % SPRINKLE_COLORS.length],
    };
  });
  const topY = a.cy - a.ry * 0.45;              // base del topper, plantada en la superficie
  return (
    <div className="cc-preview">
      <img src={img} alt="Tu cookie cake" className="cc-base" />
      <svg viewBox="0 0 600 600" className="cc-overlay" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
        <defs>
          <clipPath id={clipId}><ellipse cx={a.cx} cy={a.cy} rx={a.rx} ry={a.ry} /></clipPath>
        </defs>
        {/* sprinkles y mensaje recortados al óvalo del glaseado -> nunca se salen */}
        <g clipPath={`url(#${clipId})`}>
          {sprinkles && spr.map((s, i) => (
            <line key={i} x1={s.x} y1={s.y} x2={s.x + 14} y2={s.y} stroke={s.c} strokeWidth="5" strokeLinecap="round"
              transform={`rotate(${s.r} ${s.x} ${s.y})`} />
          ))}
          {mensaje && (
            <text x={a.cx} y={a.cy + 8} textAnchor="middle" fontFamily="'Patrick Hand', cursive" fontSize="30"
              fill={textColor} stroke={dark ? 'rgba(0,0,0,.28)' : 'rgba(255,255,255,.65)'} strokeWidth="4"
              style={{ paintOrder: 'stroke' }}>
              {mensaje.slice(0, 16)}
            </text>
          )}
        </g>
        {/* topper: clavado en la superficie del glaseado (no se recorta, sobresale) */}
        {topper && (
          <g>
            <line x1={a.cx} y1={topY} x2={a.cx} y2={topY - 64} stroke="#4A2A45" strokeWidth="4" strokeLinecap="round" />
            <path d={`M${a.cx} ${topY - 64} L${a.cx + 56} ${topY - 54} L${a.cx} ${topY - 36} Z`} fill="#FCE38A" stroke="#4A2A45" strokeWidth="3" strokeLinejoin="round" />
            <circle cx={a.cx} cy={topY - 68} r="6.5" fill="#F4B8C7" stroke="#4A2A45" strokeWidth="3" />
          </g>
        )}
      </svg>
    </div>
  );
}

export default function DisenaCookieCake() {
  const nav = useNavigate();
  const { add } = useCart();
  const [p, setP] = useState(null);
  const [err, setErr] = useState('');
  const [tamano, setTamano] = useState('');
  const [masa, setMasa] = useState('');
  const [cobertura, setCobertura] = useState('Crema');
  const [extras, setExtras] = useState(new Set());
  const [mensaje, setMensaje] = useState('');
  const [cantidad, setCantidad] = useState(1);
  const [added, setAdded] = useState(false);
  const [burst, setBurst] = useState(false);
  const [jelly, setJelly] = useState(false);

  useEffect(() => {
    api.product('cookie-cake').then((d) => {
      setP(d);
      const tam = d.opciones?.find((o) => o.tipo === 'tamaño');
      const sab = d.opciones?.find((o) => o.tipo === 'sabor');
      if (tam) setTamano(tam.valores[0].nombre);
      if (sab) setMasa(sab.valores[0].nombre);
    }).catch((e) => setErr(e.message));
  }, []);

  const opTam = p?.opciones?.find((o) => o.tipo === 'tamaño');
  const opSab = p?.opciones?.find((o) => o.tipo === 'sabor');
  const opEx = p?.opciones?.find((o) => o.tipo === 'extra');

  const precio = useMemo(() => {
    if (!p) return 0;
    let t = p.precioBase;
    t += opTam?.valores.find((v) => v.nombre === tamano)?.deltaPrecio || 0;
    extras.forEach((n) => { t += opEx?.valores.find((v) => v.nombre === n)?.deltaPrecio || 0; });
    return t;
  }, [p, tamano, extras, opTam, opEx]);

  if (err) return <div className="page"><p className="empty">{err}</p><Link to="/carta" className="btn btn--ghost">Ver la carta</Link></div>;
  if (!p) return <div className="page"><p className="empty">Cargando el taller…</p></div>;

  const toggleExtra = (n) => setExtras((prev) => {
    const s = new Set(prev); s.has(n) ? s.delete(n) : s.add(n); return s;
  });

  const addToCart = () => {
    add({
      slug: 'cookie-cake', nombre: 'Cookie Cake personalizado', imagen: p.imagenes?.[0],
      opcionesElegidas: { 'tamaño': tamano, 'sabor': masa, 'extra': [...extras], 'Cobertura': cobertura },
      textoPersonalizado: mensaje, cantidad, precioUnidad: precio,
    });
    setAdded(true); setBurst(true); setJelly(true);
    setTimeout(() => setJelly(false), 420);
    setTimeout(() => setBurst(false), 750);
    setTimeout(() => setAdded(false), 2800);
  };

  return (
    <div className="page disena">
      <div className="page-head">
        <p className="kicker hand" style={{ justifyContent: 'center', fontSize: '1.3rem' }}>
          <Doodle name="cupcake" size={26} color="var(--mint-d)" /> Tu taller dulce
        </p>
        <h1>Diseña tu cookie cake</h1>
        <p>Hazla 100% tuya: tamaño, sabor, color, extras y tu mensaje. La verás cambiar al instante.</p>
      </div>

      <div className="disena-grid">
        {/* PREVIEW */}
        <Reveal className="cc-stage" now>
          <CookiePreview masa={masa} cobertura={cobertura} tamano={tamano} sprinkles={extras.has('Sprinkles')} topper={extras.has('Topper personalizado')} mensaje={mensaje} />
          <p className="cc-stage-cap hand">Vista previa · {tamano}</p>
        </Reveal>

        {/* CONTROLES */}
        <div className="cc-controls">
          <div className="opt">
            <label><span className="step">1</span> Tamaño</label>
            <div className="cc-sizes">
              {opTam?.valores.map((v) => (
                <button key={v.nombre} className={`cc-size${tamano === v.nombre ? ' on' : ''}`} onClick={() => setTamano(v.nombre)}>
                  <strong>{v.nombre.split(' (')[0]}</strong>
                  <small>{v.nombre.match(/\(([^)]+)\)/)?.[1]}</small>
                  {v.deltaPrecio > 0 && <span className="cc-delta">+{v.deltaPrecio}€</span>}
                </button>
              ))}
            </div>
          </div>

          <div className="opt">
            <label><span className="step">2</span> Sabor de la masa</label>
            <div className="opt-vals">
              {opSab?.valores.map((v) => (
                <button key={v.nombre} className={`pill${masa === v.nombre ? ' pill--on' : ''}`} onClick={() => setMasa(v.nombre)}>
                  <span className="cc-dot" style={{ background: MASA_COLOR[v.nombre] }} /> {v.nombre}
                </button>
              ))}
            </div>
          </div>

          <div className="opt">
            <label><span className="step">3</span> Color de la cobertura</label>
            <div className="cc-swatches">
              {COBERTURAS.map((c) => (
                <button key={c.nombre} className={`cc-sw${cobertura === c.nombre ? ' on' : ''}`} onClick={() => setCobertura(c.nombre)}
                  title={c.nombre} aria-label={c.nombre}>
                  <span style={{ background: c.color }} />
                  <small>{c.nombre}</small>
                </button>
              ))}
            </div>
          </div>

          <div className="opt">
            <label><span className="step">4</span> Extras</label>
            <div className="opt-vals">
              {opEx?.valores.map((v) => (
                <button key={v.nombre} className={`pill${extras.has(v.nombre) ? ' pill--on' : ''}`} onClick={() => toggleExtra(v.nombre)}>
                  {v.nombre} {v.deltaPrecio > 0 && <span>+{v.deltaPrecio}€</span>}
                </button>
              ))}
            </div>
          </div>

          <div className="opt">
            <label><span className="step">5</span> Tu mensaje <small style={{ fontWeight: 500, color: 'var(--plum-soft)' }}>(opcional, máx. 16)</small></label>
            <input type="text" maxLength={16} value={mensaje} onChange={(e) => setMensaje(e.target.value)} placeholder="Ej: ¡Feliz cumple!" />
          </div>

          <div className="buy">
            <div className="qty">
              <button onClick={() => setCantidad((c) => Math.max(1, c - 1))}>−</button>
              <span>{cantidad}</span>
              <button onClick={() => setCantidad((c) => c + 1)}>+</button>
            </div>
            <div className="buy-price">{(precio * cantidad).toFixed(2)} €</div>
          </div>

          <div className="add-wrap">
            <button className={`btn btn--primary btn--block${jelly ? ' jelly' : ''}`} onClick={addToCart}>Añadir al pedido</button>
            <SprinkleBurst show={burst} />
          </div>
          {added && (
            <div className="added">
              <Doodle name="sparkle" size={22} color="var(--mint-d)" /> ¡En el horno!{' '}
              <button className="linklike" onClick={() => nav('/pedido')}>Ir a mi pedido →</button>
            </div>
          )}
          <p className="note">
            <Doodle name="heart" size={18} color="var(--rose)" />
            ¿Quieres algo aún más especial (formas, fotos comestibles, tartas a medida)? Pídelo como <Link to="/pedido-personalizado" className="linklike">pedido personalizado</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
